import { useEffect, useState } from "react";
import { Briefcase, Loader2, X, ArrowRight, Sparkles } from "lucide-react";
import { api, fmtDate, STATUS_STYLES } from "@/lib/api";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-sm text-navy shadow-none focus:border-gold focus:outline-none";
const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

export default function Services() {
  const [data, setData] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ details: "", preferred_date: "", source: "marketplace", context_note: "" });
  const [suggestions, setSuggestions] = useState([]);

  const load = () => {
    api.get("/api/app/services").then((r) => setData(r.data));
    api.get("/api/app/services/suggestions").then((r) => setSuggestions(r.data));
  };
  useEffect(load, []);

  const openRequest = (service, source = "marketplace", context_note = "") => {
    setRequesting(service);
    setForm({ details: "", preferred_date: "", source, context_note });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/app/services/requests", { ...form, service_id: requesting.id });
      setRequesting(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-8" data-testid="services-page">
      <div>
        <p className="eyebrow">TSM Services Marketplace</p>
        <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Professional Services from TSM Group CLE</h1>
        <p className="mt-2 max-w-2xl text-sm text-ink/60">{data.disclaimer}</p>
      </div>

      {suggestions.length > 0 && (
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-6" data-testid="service-suggestions">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-navy">
            <Sparkles className="h-5 w-5 text-golddark" /> Suggested from your workspace
          </h2>
          <div className="mt-4 space-y-3">
            {suggestions.map((s) => (
              <div key={s.service_id} className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-navy/10 bg-white p-4" data-testid="service-suggestion-item">
                <p className="max-w-xl text-sm text-navy/80">{s.message}</p>
                <button onClick={() => openRequest({ id: s.service_id, name: s.message.slice(0, 40) }, "suggestion", s.context_note)}
                        data-testid="service-suggestion-request" className="rounded-full border border-gold px-4 py-2 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold">
                  Request Assistance
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] italic text-ink/50">
            With your permission, the request includes a short context note about why it was suggested. Your business records
            are never sent to TSM without your explicit authorization.
          </p>
        </div>
      )}

      {data.catalog.map((cat) => (
        <div key={cat.category} className="rounded-lg border border-navy/10 bg-white p-6" data-testid="service-category">
          <h2 className="font-serif text-2xl font-semibold text-navy">{cat.category}</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {cat.services.map((s) => (
              <div key={s.id} className="flex h-full flex-col justify-between rounded-lg border border-navy/10 bg-paper p-5 transition-all hover:border-gold" data-testid={`service-item-${s.id}`}>
                <div>
                  <p className="font-semibold text-navy">{s.name}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/60">{s.description}</p>
                </div>
                <button onClick={() => openRequest(s)} data-testid={`service-request-${s.id}`}
                        className="mt-4 inline-flex w-fit items-center gap-2 text-sm font-bold uppercase tracking-wider text-golddark transition-colors hover:text-navy">
                  Request a quote <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="my-requests">
        <h2 className="font-serif text-2xl font-semibold text-navy">My service requests</h2>
        {data.my_requests.length === 0 ? (
          <p className="mt-4 text-sm text-ink/50">No requests yet. Pick a service above to get started.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-navy/10 font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
                  {["ID", "Service", "Requested", "Preferred date", "Status"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.my_requests.map((r) => (
                  <tr key={r.request_id} className="border-b border-navy/5" data-testid={`service-request-row-${r.request_id}`}>
                    <td className="px-3 py-3 font-mono text-xs font-semibold text-navy">{r.request_id}</td>
                    <td className="px-3 py-3 text-navy">{r.service_name}</td>
                    <td className="px-3 py-3 text-ink/60">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-3 text-ink/60">{fmtDate(r.preferred_date)}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[r.status] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {requesting && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/50" onClick={() => setRequesting(null)} data-testid="service-drawer-backdrop">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-lg overflow-y-auto bg-white p-8" data-testid="service-drawer">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-navy">Request: {requesting.name}</h2>
              <button type="button" onClick={() => setRequesting(null)} aria-label="Close" data-testid="service-drawer-close"><X className="h-5 w-5 text-navy/50" /></button>
            </div>
            <p className="mt-4 rounded-sm border border-gold/40 bg-gold/5 p-4 text-xs italic text-navy/70">
              This starts a review, not an instant booking. TSM Group CLE confirms details, scheduling, and pricing with you
              directly. Service fees are quoted separately from your subscription.
            </p>
            <div className="mt-6 space-y-5">
              <div>
                <label className={label}>What do you need? *</label>
                <textarea required className={`${field} min-h-24`} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="service-request-details" placeholder="Describe the service you need, including any deadlines" />
              </div>
              <div>
                <label className={label}>Preferred appointment date</label>
                <input type="date" className={field} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="service-request-date" />
              </div>
              {form.context_note && (
                <p className="rounded-sm border border-navy/10 bg-paper p-3 text-xs text-ink/60" data-testid="service-request-context">
                  Context attached with your permission: {form.context_note}
                </p>
              )}
              <button type="submit" disabled={busy} data-testid="service-request-submit" className="btn-gold w-full disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Briefcase className="h-4 w-4" />}
                Submit Request
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
